import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import QaRolesCustomSelectField from 'components/QaRolesCustomSelectField';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Loader } from 'ncoded-component-library';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import {
  DrawerNavigation,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import useMembers from 'router/subrouters/DataRoom/subrouters/Members/hooks/useMembers';
import useOptions from 'router/subrouters/DataRoom/subrouters/Members/hooks/useOptions';
import { normalizeEmail } from 'utils';
import useMemberRequestAccess from '../../../../hooks/useMemberRequestAccess';

import './ManageRequests.styles.scss';

type ManageRequestsProps = {
  className?: string;
  isBuySide: boolean;
  member: DataRoomMember;
  // Reviewing already updates the members table and the waiting room queue
  // through `useMemberRequestAccess`. These are only for a parent holding its
  // own copy of the member, and are optional because a parent whose copy can go
  // stale — the waiting room keeps its member so the drawer holds on to its
  // title through the closing animation — needs neither.
  updateMember?: (member: DataRoomMember) => void;
  removeMember?: (memberId: string) => void;
} & DrawerNavigation;

const ManageRequests: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageRequestsProps
> = (
  { isBuySide, member, updateMember, removeMember, onPrev, onNext },
  ref,
) => {
  const { t } = useTranslation();

  const { _id, qaRole, role, email } = member || {};

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { sellerRoleOptions, sellerQaRole, buyerRoleOptions, buyerQaRole } =
    useOptions();

  const { buySideGroups } = useMembers();

  // The member carries no group of its own — which one it belongs to is only in
  // the provider's grouping. Sell-side members belong to none, and a member the
  // provider has not caught up with yet is not in any group either, so the row
  // is only rendered once a group is actually found.
  const groupName = useMemo(
    () =>
      buySideGroups.find(({ dataRoomMemberIds }) =>
        dataRoomMemberIds?.includes(_id),
      )?.name,
    [buySideGroups, _id],
  );

  const initialData = useMemo(
    () =>
      role || qaRole
        ? {
            role,
            qaRole,
          }
        : {},
    [qaRole, role],
  );

  const classes = classNames('manage-requests', classNames);

  const { isLoading, acceptRequest, rejectRequest } = useMemberRequestAccess();

  const onSubmit = useCallback(
    async (values: Partial<DataRoomMember>) =>
      acceptRequest({ _id, ...values }, (member) => {
        updateMember?.(member);
        drawer?.setOpen(false);
      }),
    [_id, acceptRequest, drawer, updateMember],
  );

  const declineRequest = useCallback(
    async () =>
      rejectRequest(_id, () => {
        removeMember?.(_id);
        drawer?.setOpen(false);
      }),
    [_id, drawer, rejectRequest, removeMember],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      title={t(isBuySide ? 'buySideAccessRequest' : 'sellSideAccessRequest')}
      className={classes}
      onPrev={onPrev}
      onNext={onNext}
    >
      <dl className="manage-requests__details">
        <dt>{t('email')}</dt>
        <dd>{normalizeEmail(email)}</dd>

        {!!groupName && (
          <>
            <dt>{t('group')}</dt>
            <dd>{groupName}</dd>
          </>
        )}
      </dl>

      <Form
        initialValues={initialData}
        onSubmit={onSubmit}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            {isLoading && <Loader />}

            <section className="manage-requests__section">
              <h3>{t('access')}</h3>

              <div className="manage-requests__section__roles">
                <QaRolesCustomSelectField
                  name="role"
                  label={t('filesRole')}
                  options={isBuySide ? buyerRoleOptions : sellerRoleOptions}
                  renderAsPortal
                />

                <QaRolesCustomSelectField
                  name="qaRole"
                  label={t('qaRole')}
                  options={isBuySide ? buyerQaRole : sellerQaRole}
                  renderAsPortal
                />
              </div>
            </section>

            <div className="manage-requests__actions">
              <Button
                type="button"
                variant="link"
                disabled={isLoading}
                onClick={declineRequest}
              >
                {t('decline')}
              </Button>

              <Button type="submit" disabled={isLoading}>
                {t('approve')}
              </Button>
            </div>
          </form>
        )}
      />
    </SideDrawer>
  );
};

export default forwardRef(ManageRequests);
